import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('fb_access_token'));

  const axiosInstance = axios.create({
    baseURL: API,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add auth header to requests
  axiosInstance.interceptors.request.use((config) => {
    const token = localStorage.getItem('fb_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle 401 responses
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        // Try to refresh token
        const refreshToken = localStorage.getItem('fb_refresh_token');
        if (refreshToken) {
          try {
            const response = await axios.post(`${API}/auth/refresh`, {
              refresh_token: refreshToken,
            });
            localStorage.setItem('fb_access_token', response.data.access_token);
            localStorage.setItem('fb_refresh_token', response.data.refresh_token);
            error.config.headers.Authorization = `Bearer ${response.data.access_token}`;
            return axiosInstance(error.config);
          } catch (refreshError) {
            logout();
          }
        } else {
          logout();
        }
      }
      return Promise.reject(error);
    }
  );

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    
    localStorage.setItem('fb_access_token', access_token);
    localStorage.setItem('fb_refresh_token', refresh_token);
    localStorage.setItem('fb_user', JSON.stringify(userData));
    
    setAccessToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const loginWithToken = (token, userData) => {
    localStorage.setItem('fb_access_token', token);
    localStorage.setItem('fb_user', JSON.stringify(userData));
    setAccessToken(token);
    setUser(userData);
  };

  const logout = useCallback(() => {
    localStorage.removeItem('fb_access_token');
    localStorage.removeItem('fb_refresh_token');
    localStorage.removeItem('fb_user');
    localStorage.removeItem('fb_team_context');
    setAccessToken(null);
    setUser(null);
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('fb_access_token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'ADMIN';
  const isStaff = ['ADMIN', 'SNC', 'PHYSIO', 'COACH', 'NUTRITIONIST'].includes(user?.role);
  const isAthlete = user?.role === 'ATHLETE';

  const hasRole = (roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const getHomeRoute = () => {
    if (!user) return '/login';
    switch (user.role) {
      case 'SUPERADMIN':
        return '/superadmin';
      case 'ADMIN':
      case 'SNC':
      case 'COACH':
        return '/staff/roster';
      case 'PHYSIO':
        return '/staff/roster';
      case 'NUTRITIONIST':
        return '/staff/nutrition';
      case 'ATHLETE':
        return '/athlete/wellness';
      default:
        return '/login';
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        isAdmin,
        isStaff,
        isAthlete,
        login,
        loginWithToken,
        logout,
        hasRole,
        getHomeRoute,
        api: axiosInstance,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
